import { useState, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import {
  Drawer,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  TextField,
} from '@mui/material';
import CallIcon from '@mui/icons-material/Call';
import CallEndIcon from '@mui/icons-material/CallEnd';
import BackspaceIcon from '@mui/icons-material/Backspace';
import CloseIcon from '@mui/icons-material/Close';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { usePhone } from '../../contexts/PhoneContext';

interface PhoneDialerProps {
  open: boolean;
  onClose: () => void;
  requestUuid: string;
  initialNumber?: string;
}

// Dialpad button data
const DIALPAD_BUTTONS = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
];

const formatPhoneDisplay = (number: string): string => {
  const cleaned = number.replace(/\D/g, '');
  if (cleaned.length <= 3) return cleaned;
  if (cleaned.length <= 6) return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
  if (cleaned.length <= 10)
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  return `+${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
};

export const PhoneDialer = observer(function PhoneDialer({
  open,
  onClose,
  requestUuid,
  initialNumber,
}: PhoneDialerProps) {
  const {
    phoneReady,
    activeCallKey,
    activeCall,
    activeCallParticipants,
    phoneMakeCall,
    phoneHangUp,
    phoneAddParticipant,
    phoneRemoveParticipant,
    sendDTMF,
    fetchCallParticipants,
  } = usePhone();

  const [phoneNumber, setPhoneNumber] = useState('');
  const [calling, setCalling] = useState(false);
  // When non-null, we're in "adding participant" mode. The value is the original number to restore on cancel.
  const [savedNumber, setSavedNumber] = useState<string | null>(null);

  const notesKey = `phone_notes_${requestUuid}`;
  const [notes, setNotes] = useState(() => localStorage.getItem(notesKey) || '');

  // Sync notes to localStorage on change
  useEffect(() => {
    if (notes) {
      localStorage.setItem(notesKey, notes);
    } else {
      localStorage.removeItem(notesKey);
    }
  }, [notes, notesKey]);

  const isOnCall = !!activeCallKey;
  const isAddingParticipant = savedNumber !== null;

  const ENDED_STATUSES = ['completed', 'complete', 'failed', 'canceled', 'no-answer', 'busy'];
  const liveParticipants = activeCallParticipants.filter(
    (p) => !ENDED_STATUSES.includes(p.twilio_status?.toLowerCase())
  );

  const handleDigitPress = useCallback(
    (digit: string) => {
      if (isOnCall && !isAddingParticipant) {
        // Send DTMF tone during active call (IVR navigation)
        sendDTMF(digit);
      } else {
        // Build phone number (idle or adding participant)
        setPhoneNumber((prev) => prev + digit);
      }
    },
    [isOnCall, isAddingParticipant, sendDTMF]
  );

  const handleBackspace = useCallback(() => {
    setPhoneNumber((prev) => prev.slice(0, -1));
  }, []);

  const handleCall = useCallback(async () => {
    if (!phoneNumber.trim() || !phoneReady) return;

    setCalling(true);
    try {
      await phoneMakeCall(phoneNumber, 'request', requestUuid);
      setPhoneNumber('');
    } catch (err) {
      console.error('Call failed:', err);
    } finally {
      setCalling(false);
    }
  }, [phoneNumber, phoneReady, phoneMakeCall, requestUuid]);

  const handleHangUp = useCallback(() => {
    phoneHangUp();
    setPhoneNumber('');
    setSavedNumber(null);
    setCalling(false);
  }, [phoneHangUp]);

  const handleStartAddingParticipant = useCallback(() => {
    setSavedNumber(phoneNumber);
    setPhoneNumber('');
  }, [phoneNumber]);

  const handleCancelAddParticipant = useCallback(() => {
    setPhoneNumber(savedNumber || '');
    setSavedNumber(null);
  }, [savedNumber]);

  const handleAddParticipant = useCallback(async () => {
    if (!phoneNumber.trim() || !activeCallKey) return;

    setCalling(true);
    try {
      await phoneAddParticipant(phoneNumber, activeCallKey);
      setPhoneNumber(savedNumber || '');
      setSavedNumber(null);
    } catch (err) {
      console.error('Add participant failed:', err);
    } finally {
      setCalling(false);
    }
  }, [phoneNumber, activeCallKey, phoneAddParticipant, savedNumber]);

  // Fetch participants when on a call
  useEffect(() => {
    if (!activeCallKey || !open) return;
    fetchCallParticipants(activeCallKey);
    // Poll for participant status updates while on a call
    const interval = setInterval(() => {
      fetchCallParticipants(activeCallKey);
    }, 5000);
    return () => clearInterval(interval);
  }, [activeCallKey, open, fetchCallParticipants]);

  // When opened with an initial number, populate the input
  useEffect(() => {
    if (open && initialNumber && !isOnCall) {
      setPhoneNumber(initialNumber);
    }
  }, [open, initialNumber]); // eslint-disable-line react-hooks/exhaustive-deps

  // If the call ends while adding a participant, reset to idle
  useEffect(() => {
    if (!isOnCall && isAddingParticipant) {
      setPhoneNumber('');
      setSavedNumber(null);
    }
  }, [isOnCall, isAddingParticipant]);

  const notesRef = useRef<HTMLTextAreaElement>(null);

  // Handle keyboard digit input when notes field isn't focused
  useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't capture keys when typing in the notes field
      if (notesRef.current?.contains(e.target as Node)) return;

      const VALID_DIGITS = '0123456789*#';
      if (VALID_DIGITS.includes(e.key)) {
        e.preventDefault();
        handleDigitPress(e.key);
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        handleBackspace();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [open, handleDigitPress, handleBackspace]);

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      if (notesRef.current?.contains(e.target as Node)) return;
      if (isOnCall && !isAddingParticipant) return;
      e.preventDefault();
      const digits = e.clipboardData.getData('text').replace(/\D/g, '');
      if (digits) {
        setPhoneNumber(digits);
      }
    },
    [isOnCall, isAddingParticipant]
  );

  const handleClose = useCallback(() => {
    if (!isOnCall) {
      setPhoneNumber('');
    }
    onClose();
  }, [isOnCall, onClose]);

  return (
    <Drawer
      anchor="top"
      open={open}
      onClose={handleClose}
      PaperProps={{
        sx: {
          maxWidth: 360,
          mx: 'auto',
          borderRadius: '0 0 16px 16px',
        },
      }}
    >
      <Box sx={{ p: 2 }} onPaste={handlePaste}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {isAddingParticipant ? 'Add Participant' : isOnCall ? 'Active Call' : 'Phone'}
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Call Status / Participants */}
        {isOnCall && (
          <Box sx={{ mb: 2 }}>
            {liveParticipants.length > 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {liveParticipants.map((p) => {
                  const status = p.twilio_status?.toLowerCase();
                  const isConnected = status === 'in-progress' || status === 'connected';
                  return (
                    <Box
                      key={p.key}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        borderRadius: 1,
                        bgcolor: 'action.hover',
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: isConnected ? 'success.main' : 'warning.main',
                          flexShrink: 0,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {formatPhoneDisplay(p.phone_number)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {p.twilio_status || 'connecting'}
                        </Typography>
                      </Box>
                      <IconButton
                        size="small"
                        onClick={async () => {
                          await phoneRemoveParticipant(activeCallKey!, p.twilio_call_sid);
                          if (liveParticipants.length <= 1) {
                            handleHangUp();
                          }
                        }}
                        title="Remove participant"
                      >
                        <PersonRemoveIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>
            ) : (
              <Box sx={{ textAlign: 'center' }}>
                <Chip
                  label={activeCall?.twilio_status || 'Connecting...'}
                  color="success"
                  sx={{ mb: 1 }}
                />
                <Typography variant="body2" color="text.secondary">
                  {formatPhoneDisplay(activeCall?.number_to || '')}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Phone Number Display */}
        <Box
          sx={{
            textAlign: 'center',
            mb: 2,
            minHeight: 48,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Typography
            variant="h4"
            sx={{ fontWeight: 300, letterSpacing: 2, fontFamily: 'monospace' }}
          >
            {formatPhoneDisplay(phoneNumber) || (isOnCall && !isAddingParticipant ? '' : 'Enter number')}
          </Typography>
          {phoneNumber && (!isOnCall || isAddingParticipant) && (
            <IconButton onClick={handleBackspace} sx={{ ml: 1 }}>
              <BackspaceIcon />
            </IconButton>
          )}
        </Box>

        {/* Dialpad */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 1,
            mb: 2,
          }}
        >
          {DIALPAD_BUTTONS.map(({ digit, letters }) => (
            <Button
              key={digit}
              variant="outlined"
              onClick={() => handleDigitPress(digit)}
              sx={{
                height: 64,
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 2,
              }}
            >
              <Typography variant="h5">{digit}</Typography>
              {letters && (
                <Typography variant="caption" color="text.secondary">
                  {letters}
                </Typography>
              )}
            </Button>
          ))}
        </Box>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
          {isAddingParticipant ? (
            <>
              {/* Dial the new participant */}
              <Button
                variant="contained"
                color="success"
                startIcon={<CallIcon />}
                onClick={handleAddParticipant}
                disabled={!phoneNumber.trim() || calling}
                sx={{ flex: 1 }}
              >
                {calling ? 'Dialing...' : 'Dial Call'}
              </Button>
              {/* Cancel adding participant */}
              <Button
                variant="outlined"
                onClick={handleCancelAddParticipant}
                disabled={calling}
                sx={{ flex: 1 }}
              >
                Nevermind
              </Button>
            </>
          ) : isOnCall ? (
            <>
              {/* Enter add-participant mode */}
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PersonAddIcon />}
                onClick={handleStartAddingParticipant}
                sx={{ flex: 1 }}
              >
                Add Participant
              </Button>
              {/* Hang Up */}
              <Button
                variant="contained"
                color="error"
                startIcon={<CallEndIcon />}
                onClick={handleHangUp}
                sx={{ flex: 1 }}
              >
                End Call
              </Button>
            </>
          ) : (
            /* Make Call */
            <Button
              variant="contained"
              color="success"
              startIcon={<CallIcon />}
              onClick={handleCall}
              disabled={!phoneNumber.trim() || !phoneReady || calling}
              sx={{ flex: 1, py: 1.5 }}
            >
              {calling ? 'Calling...' : 'Call'}
            </Button>
          )}
        </Box>

        {/* Call Notes */}
        <TextField
          inputRef={notesRef}
          multiline
          minRows={2}
          maxRows={6}
          placeholder="Call notes (only visible to you)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 2 }}
        />

        {!phoneReady && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 2 }}
          >
            Phone system initializing...
          </Typography>
        )}
      </Box>
    </Drawer>
  );
});
